import React, { useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Mic, Power, Monitor, Cable, FileText, Download, Printer, 
  Music, MapPin, Calendar, Users, Zap, Speaker, UserCircle,
  Save, Loader2, CheckCircle, MapPinned, Navigation
} from "lucide-react";
import { toast } from "sonner";
import { 
  generateTechRider, 
  getDefaultPositions,
  getEquipmentByZone,
  getPositioningSummary,
  POSITION_LABELS,
  POSITION_REQUIREMENTS,
  ZONE_LABELS,
  ZONE_DESCRIPTIONS,
  type TechRider, 
  type InstrumentTechSpec, 
  type MusicianPosition,
  type EventZone,
  type InstrumentWithPosition 
} from "@/lib/techRiderData";
import { StageDiagram } from "./StageDiagram";
import { logActivity } from "@/lib/activityLogger";
import { generateDocumentNumber } from "@/lib/documentNaming";
import { generateTechRiderPdf } from "@/lib/nativePdfGenerator";

interface TechRiderGeneratorProps {
  instruments: string[];
  eventName: string;
  eventDate: string;
  venue?: string;
  inquiryId?: string;
  clientName?: string;
  clientEmail?: string;
  isComplete?: boolean; // Whether ensemble is fully selected
  onSaved?: () => void;
}

function InstrumentTechCard({ 
  spec, 
  position,
  zone,
  roamingNotes,
  onPositionChange,
  onZoneChange,
  onRoamingNotesChange,
}: { 
  spec: InstrumentTechSpec; 
  position: MusicianPosition;
  zone: EventZone;
  roamingNotes?: string;
  onPositionChange: (pos: MusicianPosition) => void;
  onZoneChange: (zone: EventZone) => void;
  onRoamingNotesChange: (notes: string) => void;
}) {
  return (
    <Card className="border-border/50">
      <CardHeader className="pb-2 pt-3 px-3">
        <CardTitle className="text-sm font-semibold flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Music className="h-4 w-4 text-primary" />
            {spec.instrument}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3 space-y-2 text-xs">
        {/* Zone selector */}
        <div className="flex items-center gap-2 bg-primary/10 rounded px-2 py-1.5">
          <MapPinned className="h-3.5 w-3.5 text-primary shrink-0" />
          <Select value={zone} onValueChange={(v) => onZoneChange(v as EventZone)}>
            <SelectTrigger className="h-6 text-xs border-0 bg-background p-1 focus:ring-0 shadow-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              {Object.entries(ZONE_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key} className="text-xs">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Position selector */}
        <div className="flex items-center gap-2 bg-muted/50 rounded px-2 py-1.5">
          <UserCircle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <Select value={position} onValueChange={(v) => onPositionChange(v as MusicianPosition)}>
            <SelectTrigger className="h-6 text-xs border-0 bg-background p-1 focus:ring-0 shadow-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              {Object.entries(POSITION_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key} className="text-xs">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Roaming notes (show for roaming or mobile positions) */}
        {(zone === 'roaming' || position === 'mobile') && (
          <div className="mt-1">
            <input
              type="text"
              placeholder="Roaming notes (e.g., 'moves through cocktail space')"
              value={roamingNotes || ''}
              onChange={(e) => onRoamingNotesChange(e.target.value)}
              className="w-full text-xs px-2 py-1 rounded border border-border bg-background"
            />
          </div>
        )}

        {/* Microphone */}
        {spec.microphone.type !== 'N/A (direct)' && (
          <div className="flex items-start gap-2">
            <Mic className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">{spec.microphone.type}</p>
              <p className="text-muted-foreground">{spec.microphone.model}</p>
              {spec.microphone.notes && (
                <p className="text-muted-foreground/80 italic">{spec.microphone.notes}</p>
              )}
            </div>
          </div>
        )}

        {/* DI Box */}
        {spec.diBox?.required && (
          <div className="flex items-start gap-2">
            <Zap className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">DI Box Required</p>
              {spec.diBox.type && <p className="text-muted-foreground">{spec.diBox.type}</p>}
            </div>
          </div>
        )}

        {/* Power */}
        {spec.power?.required && (
          <div className="flex items-start gap-2">
            <Power className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">{spec.power.outlets} power outlet(s)</p>
              {spec.power.notes && <p className="text-muted-foreground">{spec.power.notes}</p>}
            </div>
          </div>
        )}

        {/* Monitor */}
        {spec.monitor && (
          <div className="flex items-start gap-2">
            <Speaker className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">{spec.monitor.type}</p>
              {spec.monitor.notes && <p className="text-muted-foreground">{spec.monitor.notes}</p>}
            </div>
          </div>
        )}

        {/* Cables */}
        {spec.cables && spec.cables.length > 0 && (
          <div className="flex items-start gap-2">
            <Cable className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div className="flex flex-wrap gap-1">
              {spec.cables.map((cable, i) => (
                <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">
                  {cable}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Stage notes */}
        {spec.stageNotes && (
          <p className="text-muted-foreground bg-muted/30 rounded px-2 py-1 mt-1">
            📍 {spec.stageNotes}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function TechRiderContent({ 
  rider, 
  instrumentPositions,
  onPositionChange,
  onZoneChange,
  onRoamingNotesChange,
  setupNotes,
  onSetupNotesChange 
}: { 
  rider: TechRider; 
  instrumentPositions: InstrumentWithPosition[];
  onPositionChange: (index: number, position: MusicianPosition) => void;
  onZoneChange: (index: number, zone: EventZone) => void;
  onRoamingNotesChange: (index: number, notes: string) => void;
  setupNotes: string;
  onSetupNotesChange: (notes: string) => void;
}) {
  const equipmentByZone = useMemo(() => getEquipmentByZone(instrumentPositions), [instrumentPositions]);
  const positioningSummary = useMemo(() => getPositioningSummary(instrumentPositions), [instrumentPositions]);
  const activeZones = Object.entries(equipmentByZone).filter(([_, data]) => data.musicians.length > 0);
  
  return (
    <div className="space-y-4">
      {/* Header info */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span>{rider.date}</span>
        </div>
        {rider.venue && (
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{rider.venue}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span>{rider.instruments.length} musicians</span>
        </div>
        <div className="flex items-center gap-2">
          <Mic className="h-4 w-4 text-muted-foreground" />
          <span>{rider.totalInputChannels} input channels</span>
        </div>
      </div>

      <Separator />

      {/* Zone summary badges */}
      <div className="flex flex-wrap gap-2">
        {activeZones.map(([zone, data]) => (
          <Badge key={zone} variant="outline" className="gap-1 bg-primary/10">
            <MapPinned className="h-3 w-3" />
            {ZONE_LABELS[zone as EventZone]}: {data.musicians.length}
          </Badge>
        ))}
      </div>

      {/* Summary badges */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="secondary" className="gap-1">
          <Mic className="h-3 w-3" />
          {rider.totalInputChannels} Channels
        </Badge>
        {rider.totalPowerOutlets > 0 && (
          <Badge variant="secondary" className="gap-1">
            <Power className="h-3 w-3" />
            {rider.totalPowerOutlets} Power Outlets
          </Badge>
        )}
        <Badge variant="secondary" className="gap-1">
          <Monitor className="h-3 w-3" />
          {rider.instruments.length} Monitor Mixes
        </Badge>
      </div>

      <Separator />

      {/* SECTION 1: Equipment List for Production Team */}
      <div className="bg-muted/30 rounded-lg p-3">
        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          Equipment List (for Production/AV Team)
        </h4>
        
        {activeZones.map(([zone, data]) => (
          <div key={zone} className="mb-4 last:mb-0">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="default" className="text-xs">
                {ZONE_LABELS[zone as EventZone]}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {data.totalChannels} ch / {data.totalPower} power / {data.totalMonitors} monitors
              </span>
            </div>
            <div className="ml-3 space-y-1.5">
              {data.musicians.map((m, i) => (
                <div key={i} className="text-xs flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>
                    <span className="font-medium">{m.name}</span>
                    <span className="text-muted-foreground"> — {m.mic}</span>
                    {m.di && <span className="text-muted-foreground"> + DI</span>}
                    {m.power > 0 && <span className="text-muted-foreground"> + {m.power} power</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
        
        {/* Always include laptop requirement */}
        <div className="mt-3 pt-2 border-t border-border">
          <div className="text-xs flex items-start gap-2">
            <span className="text-primary">★</span>
            <span>
              <span className="font-medium">Regional Manager Laptop</span>
              <span className="text-muted-foreground"> — Stereo DI (Radial ProD2) + 3.5mm to 1/4" TRS AUX cable</span>
            </span>
          </div>
        </div>
      </div>

      <Separator />

      {/* SECTION 2: Positioning Details for Venue/Planners */}
      <div className="bg-accent/20 rounded-lg p-3">
        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
          <Navigation className="h-4 w-4 text-primary" />
          Positioning Details (for Venue POC / Planner)
        </h4>
        
        <div className="space-y-2 text-sm">
          {activeZones.map(([zone, data]) => (
            <div key={zone} className="pb-2 border-b border-border/50 last:border-0 last:pb-0">
              <div className="font-medium text-primary mb-1">{ZONE_LABELS[zone as EventZone]}</div>
              <p className="text-xs text-muted-foreground mb-1.5">{ZONE_DESCRIPTIONS[zone as EventZone]}</p>
              <ul className="space-y-1">
                {data.musicians.map((m, i) => (
                  <li key={i} className="text-xs flex items-start gap-2">
                    <span className="text-muted-foreground">→</span>
                    <span>
                      <span className="font-medium">{m.name}</span>
                      <span className="text-muted-foreground"> ({POSITION_LABELS[m.position]})</span>
                      {m.notes && <span className="italic text-primary/80"> — {m.notes}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        {/* Position requirements summary */}
        <div className="mt-3 pt-2 border-t border-border/50">
          <p className="text-xs text-muted-foreground font-medium mb-1">Furniture/Setup Requirements:</p>
          <ul className="text-xs space-y-0.5">
            {Object.entries(rider.positionSummary)
              .filter(([_, count]) => count > 0)
              .map(([pos, count]) => (
                <li key={pos} className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>{count}x {POSITION_REQUIREMENTS[pos as MusicianPosition]}</span>
                </li>
              ))}
          </ul>
        </div>
      </div>

      <Separator />

      {/* Stage Diagram */}
      <StageDiagram instruments={instrumentPositions} />

      <Separator />

      {/* Instrument details */}
      <div>
        <h4 className="font-semibold text-sm mb-2">Edit Instruments</h4>
        <div className="grid gap-2 md:grid-cols-2">
          {rider.instruments.map((spec, i) => (
            <InstrumentTechCard 
              key={i} 
              spec={spec} 
              position={instrumentPositions[i]?.position || spec.defaultPosition}
              zone={instrumentPositions[i]?.zone || 'main-stage'}
              roamingNotes={instrumentPositions[i]?.roamingNotes}
              onPositionChange={(pos) => onPositionChange(i, pos)}
              onZoneChange={(zone) => onZoneChange(i, zone)}
              onRoamingNotesChange={(notes) => onRoamingNotesChange(i, notes)}
            />
          ))}
        </div>
      </div>

      <Separator />

      {/* Setup Notes */}
      <div>
        <h4 className="font-semibold text-sm mb-2">Additional Setup Notes</h4>
        <Textarea
          placeholder="Add any special positioning requirements, venue-specific notes, or details to discuss with the AV team..."
          value={setupNotes}
          onChange={(e) => onSetupNotesChange(e.target.value)}
          className="min-h-[80px] text-sm"
        />
        <p className="text-[10px] text-muted-foreground mt-1">
          These notes will be included in the downloaded tech rider
        </p>
      </div>

      <Separator />

      {/* General requirements */}
      <div>
        <h4 className="font-semibold text-sm mb-2">General Requirements</h4>
        <ul className="text-sm space-y-1">
          {rider.generalRequirements.map((req, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>{req}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Stage plot notes */}
      <div>
        <h4 className="font-semibold text-sm mb-2">Stage Setup Notes</h4>
        <ul className="text-sm space-y-1">
          {rider.stageplotNotes.map((note, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>{note}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function TechRiderGenerator({ 
  instruments, 
  eventName, 
  eventDate, 
  venue,
  inquiryId,
  clientName,
  clientEmail,
  isComplete = false,
  onSaved,
}: TechRiderGeneratorProps) {
  const [instrumentPositions, setInstrumentPositions] = useState<InstrumentWithPosition[]>(() => 
    getDefaultPositions(instruments)
  );
  const [setupNotes, setSetupNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(false);

  // Update positions when instruments change
  React.useEffect(() => {
    setInstrumentPositions(prev => {
      // Keep existing positions for instruments that still exist
      const newPositions = instruments.map(name => {
        const existing = prev.find(p => p.name === name);
        if (existing) return existing;
        return getDefaultPositions([name])[0];
      });
      return newPositions;
    });
  }, [instruments]);

  const handlePositionChange = (index: number, position: MusicianPosition) => {
    setInstrumentPositions(prev => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index] = { ...updated[index], position };
      }
      return updated;
    });
  };

  const handleZoneChange = (index: number, zone: EventZone) => {
    setInstrumentPositions(prev => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index] = { ...updated[index], zone };
      }
      return updated;
    });
  };

  const handleRoamingNotesChange = (index: number, notes: string) => {
    setInstrumentPositions(prev => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index] = { ...updated[index], roamingNotes: notes };
      }
      return updated;
    });
  };

  const rider = useMemo(() => {
    if (instruments.length === 0) return null;
    return generateTechRider(instrumentPositions, eventName, eventDate, venue, setupNotes);
  }, [instrumentPositions, eventName, eventDate, venue, setupNotes]);

  const generateHtmlContent = (): string => {
    if (!rider) return '';
    
    const positionRows = rider.instruments.map((spec, i) => {
      const pos = instrumentPositions[i]?.position || spec.defaultPosition;
      return `
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; font-weight: 600;">${spec.instrument}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${POSITION_LABELS[pos]}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${spec.microphone.type !== 'N/A (direct)' ? `${spec.microphone.type}<br><small>${spec.microphone.model}</small>` : 'Direct (DI)'}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${spec.monitor?.type || '-'}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${spec.stageNotes || '-'}</td>
        </tr>
      `;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Tech Rider - ${rider.eventName}</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; max-width: 900px; margin: 0 auto; padding: 40px 20px; color: #333; }
          .header { text-align: center; margin-bottom: 40px; border-bottom: 3px solid #c8a45c; padding-bottom: 20px; }
          .header h1 { color: #1a1a1a; margin: 0 0 10px; font-size: 28px; }
          .header .subtitle { color: #666; font-size: 14px; }
          .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 30px; background: #f8f8f8; padding: 20px; border-radius: 8px; }
          .meta-item { display: flex; align-items: center; gap: 8px; }
          .meta-label { font-weight: 600; color: #555; }
          .summary { display: flex; gap: 20px; margin-bottom: 30px; flex-wrap: wrap; }
          .summary-badge { background: #c8a45c; color: white; padding: 8px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background: #1a1a1a; color: white; padding: 12px 8px; text-align: left; font-size: 12px; text-transform: uppercase; }
          td { padding: 8px; border: 1px solid #ddd; font-size: 13px; }
          tr:nth-child(even) { background: #f9f9f9; }
          .section { margin: 30px 0; }
          .section h2 { color: #1a1a1a; font-size: 16px; border-bottom: 2px solid #c8a45c; padding-bottom: 8px; margin-bottom: 15px; }
          ul { margin: 0; padding-left: 20px; }
          li { margin: 6px 0; font-size: 13px; }
          .notes-box { background: #fff3cd; border: 1px solid #ffc107; padding: 15px; border-radius: 8px; margin: 20px 0; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #888; font-size: 11px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🎵 TECHNICAL RIDER</h1>
          <div class="subtitle">The Lost Strings Company</div>
        </div>
        
        <div class="meta">
          <div class="meta-item"><span class="meta-label">Event:</span> ${rider.eventName}</div>
          <div class="meta-item"><span class="meta-label">Date:</span> ${rider.date}</div>
          ${rider.venue ? `<div class="meta-item"><span class="meta-label">Venue:</span> ${rider.venue}</div>` : ''}
          <div class="meta-item"><span class="meta-label">Musicians:</span> ${rider.instruments.length}</div>
        </div>
        
        <div class="summary">
          <span class="summary-badge">📊 ${rider.totalInputChannels} Input Channels</span>
          ${rider.totalPowerOutlets > 0 ? `<span class="summary-badge">⚡ ${rider.totalPowerOutlets} Power Outlets</span>` : ''}
          <span class="summary-badge">🔊 ${rider.instruments.length} Monitor Mixes</span>
        </div>
        
        <div class="section">
          <h2>Instrument Requirements</h2>
          <table>
            <thead>
              <tr>
                <th>Instrument</th>
                <th>Position</th>
                <th>Microphone</th>
                <th>Monitor</th>
                <th>Stage Notes</th>
              </tr>
            </thead>
            <tbody>
              ${positionRows}
            </tbody>
          </table>
        </div>
        
        ${setupNotes ? `
        <div class="notes-box">
          <strong>⚠️ Additional Setup Notes:</strong><br>
          ${setupNotes.replace(/\n/g, '<br>')}
        </div>
        ` : ''}
        
        <div class="section">
          <h2>General Requirements</h2>
          <ul>
            ${rider.generalRequirements.map(req => `<li>${req}</li>`).join('')}
          </ul>
        </div>
        
        <div class="section">
          <h2>Stage Setup Notes</h2>
          <ul>
            ${rider.stageplotNotes.map(note => `<li>${note}</li>`).join('')}
          </ul>
        </div>
        
        <div class="footer">
          Generated by The Lost Strings Company CRM<br>
          ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </body>
      </html>
    `;
  };

  const handleSaveToDocuments = async () => {
    if (!rider || !inquiryId) {
      toast.error("Missing inquiry information");
      return;
    }

    setSaving(true);
    try {
      // Build tech rider data for native PDF generation
      const techRiderData = {
        documentNumber: generateDocumentNumber('TechRider', eventDate),
        eventName,
        eventDate,
        venue,
        instruments: rider.instruments.map((spec, i) => ({
          name: spec.instrument,
          position: POSITION_LABELS[instrumentPositions[i]?.position || spec.defaultPosition],
          zone: ZONE_LABELS[instrumentPositions[i]?.zone || 'main-stage'],
          microphone: spec.microphone.type,
          microphoneModel: spec.microphone.model,
          diRequired: spec.diBox?.required || false,
          powerOutlets: spec.power?.outlets || 0,
          monitor: spec.monitor?.type,
          stageNotes: spec.stageNotes,
          roamingNotes: instrumentPositions[i]?.roamingNotes,
        })),
        totalChannels: rider.totalInputChannels,
        totalPowerOutlets: rider.totalPowerOutlets,
        generalRequirements: rider.generalRequirements,
        stageplotNotes: rider.stageplotNotes,
        setupNotes,
      };

      // Generate native PDF
      const contractNumber = techRiderData.documentNumber;
      const pdfBlob = generateTechRiderPdf(techRiderData);
      const fileName = `${contractNumber}.pdf`;
      const storagePath = `${inquiryId}/${fileName}`;
      
      // Upload PDF to storage
      const { error: uploadError } = await supabase.storage
        .from('contracts')
        .upload(storagePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });
        
      if (uploadError) throw uploadError;
      
      // Create document record with event date and occasion for proper grouping
      const insertData = {
        inquiry_id: inquiryId,
        contract_number: contractNumber,
        client_name: clientName || 'Client',
        client_email: clientEmail || '',
        event_details: JSON.parse(JSON.stringify({
          eventName,
          eventDate,
          occasion: eventName, // Use eventName as the occasion for grouping
          venue,
          type: 'tech_rider',
          instruments: instrumentPositions,
          setupNotes,
        })) as Json,
        packages: JSON.parse(JSON.stringify(rider.instruments.map(i => ({ instrument: i.instrument })))) as Json,
        total_amount: 0,
        status: 'draft',
        storage_path: storagePath,
      };
      
      const { error: dbError } = await supabase
        .from('inquiry_contracts')
        .insert([insertData]);
        
      if (dbError) throw dbError;
      
      // Log activity (hidden from UI but stored for audit)
      await logActivity({
        inquiryId,
        action: 'document_created',
        description: `Tech Rider PDF generated for ${eventName}`,
        metadata: { documentType: 'tech_rider', contractNumber },
      });
      
      setSaved(true);
      toast.success("Tech Rider saved as PDF to Documents");
      onSaved?.();
      
      // Reset saved state after a moment
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving tech rider:', error);
      toast.error("Failed to save Tech Rider");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    if (!rider) return;
    
    // Generate text version
    let text = `TECH RIDER\n${'='.repeat(50)}\n\n`;
    text += `Event: ${rider.eventName}\n`;
    text += `Date: ${rider.date}\n`;
    if (rider.venue) text += `Venue: ${rider.venue}\n`;
    text += `Musicians: ${rider.instruments.length}\n`;
    text += `Input Channels Required: ${rider.totalInputChannels}\n`;
    text += `Power Outlets Required: ${rider.totalPowerOutlets}\n\n`;
    
    text += `INSTRUMENT REQUIREMENTS\n${'-'.repeat(50)}\n\n`;
    rider.instruments.forEach((spec, i) => {
      const pos = instrumentPositions[i]?.position || spec.defaultPosition;
      text += `${spec.instrument}\n`;
      text += `  Position: ${POSITION_LABELS[pos]}\n`;
      text += `    → ${POSITION_REQUIREMENTS[pos]}\n`;
      if (spec.microphone.type !== 'N/A (direct)') {
        text += `  Microphone: ${spec.microphone.type} - ${spec.microphone.model}\n`;
        if (spec.microphone.notes) text += `    Notes: ${spec.microphone.notes}\n`;
      }
      if (spec.diBox?.required) text += `  DI Box: ${spec.diBox.type || 'Required'}\n`;
      if (spec.power?.required) text += `  Power: ${spec.power.outlets} outlet(s)\n`;
      if (spec.monitor) text += `  Monitor: ${spec.monitor.type}\n`;
      if (spec.cables) text += `  Cables: ${spec.cables.join(', ')}\n`;
      if (spec.stageNotes) text += `  Stage: ${spec.stageNotes}\n`;
      text += '\n';
    });
    
    if (setupNotes.trim()) {
      text += `ADDITIONAL SETUP NOTES\n${'-'.repeat(50)}\n`;
      text += `${setupNotes}\n\n`;
    }
    
    text += `GENERAL REQUIREMENTS\n${'-'.repeat(50)}\n`;
    rider.generalRequirements.forEach(req => {
      text += `• ${req}\n`;
    });
    
    text += `\nSTAGE SETUP NOTES\n${'-'.repeat(50)}\n`;
    rider.stageplotNotes.forEach(note => {
      text += `• ${note}\n`;
    });
    
    // Download
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tech-rider-${rider.date.replace(/\//g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Don't show button if ensemble is not complete
  if (!isComplete || instruments.length === 0) {
    return null;
  }

  if (!rider) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 h-6 text-xs">
          <FileText className="h-3 w-3" />
          Tech Rider
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Tech Rider: {rider.eventName}
            </span>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={handlePrint} className="h-7 px-2">
                <Printer className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDownload} className="h-7 px-2">
                <Download className="h-4 w-4" />
              </Button>
              {inquiryId && (
                <Button 
                  variant={saved ? "ghost" : "default"}
                  size="sm" 
                  onClick={handleSaveToDocuments} 
                  disabled={saving || saved}
                  className="h-7 px-3 gap-1.5"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : saved ? (
                    <CheckCircle className="h-4 w-4 text-primary" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saved ? 'Saved' : 'Save to Docs'}
                </Button>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(85vh-100px)] pr-4">
          <TechRiderContent 
            rider={rider} 
            instrumentPositions={instrumentPositions}
            onPositionChange={handlePositionChange}
            onZoneChange={handleZoneChange}
            onRoamingNotesChange={handleRoamingNotesChange}
            setupNotes={setupNotes}
            onSetupNotesChange={setSetupNotes}
          />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
